// PaymentIntelligenceService.ts — Sprint 093: Live Payment Intelligence™
// BUILD: 1.84.0
//
// SINGLE RESPONSIBILITY:
//   Convert MissionSession history into live data for PaymentCommandCard.
//   No demo data. No hardcoded values.
//   Empty states are returned when no session history exists.
//
// ARCHITECTURE:
//   Pure TypeScript. No React. No fetch. No side effects.
//   Reads from MissionSessionService (localStorage: nomad:sessions:v1).
//   Called from Home.tsx on every saveVersion increment.
//
// DATABASE PRINCIPLE (Sprint 093):
//   Historical data is NEVER overwritten. Always append. Always preserve history.
//   All analytics are calculated from historical records.
//
// DO NOT MODIFY:
//   MissionSessionService · OperatorMemoryService · IntelligenceEngine ·
//   TrustEngine · LearningEngine · Business Logic · Database Schema.

import {
  getAllSessions,
  TERMINAL_STATUSES,
  type MissionSession,
} from '@/services/MissionSessionService';
import type {
  PaymentRecord,
  LivePaymentData,
  PaymentWeekBar,
  ComingThisWeekItem,
  RecentPaymentItem,
  PaymentActionItem,
  MonthlyPaymentStats,
} from '@/components/PaymentCommandCard';

// Re-export for consumers that only import from the service layer.
export type {
  LivePaymentData,
  PaymentWeekBar,
  ComingThisWeekItem,
  RecentPaymentItem,
  PaymentActionItem,
} from '@/components/PaymentCommandCard';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
// ISO week starts Monday
const WEEK_ORDER = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The ISO date of the Monday that starts the current week. */
function thisWeekMonday(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun…6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/** Formatted date label: "TODAY", "TOMORROW", "FRI, JUL 18", etc. */
function formatDateLabel(isoDate: string): string {
  const today = isoToday();
  const tomorrow = isoDateOffset(1);
  if (isoDate === today)    return 'TODAY';
  if (isoDate === tomorrow) return 'TOMORROW';
  const d = new Date(isoDate + 'T12:00:00');
  const days  = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
}

/** Human-readable date string: "Jul 18, 2026". */
function formatDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + 'T12:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Derives an estimated payment date from a session (endedAt + ~7 days). */
function estimatedPaymentDate(session: MissionSession): string {
  const base = session.endedAt ?? session.startedAt;
  const d = new Date(base);
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

/** Derives a PaymentStatus label from session state. */
function derivePaymentStatus(session: MissionSession): import('@/components/PaymentCommandCard').PaymentStatus {
  if (session.status === 'Paid' || session.paymentStatus === 'Immediate') return 'completed';
  if (session.status === 'Submitted')                                      return 'submitted';
  if (session.paymentStatus === 'Pending')                                 return 'waiting';
  if (session.status === 'Working')                                        return 'in_progress';
  if (session.status === 'Paused')                                         return 'paused';
  if (session.status === 'Completed' || session.status === 'Applied') {
    return session.earningsActual != null && session.earningsActual > 0
      ? 'waiting'
      : 'completed';
  }
  return 'waiting';
}

/** Returns payment status label for "Coming This Week" display. */
function paymentStatusLabel(status: ReturnType<typeof derivePaymentStatus>): string {
  switch (status) {
    case 'completed':   return 'Payment Received';
    case 'submitted':   return 'Submitted';
    case 'waiting':     return 'Waiting Approval';
    case 'in_progress': return 'In Progress';
    case 'paused':      return 'On Hold';
    case 'ready':       return 'Ready to Pay';
    default:            return 'Pending';
  }
}

/** Infers a payment method label. Falls back to 'PayPal' (most common). */
function inferPaymentMethod(session: MissionSession): string {
  if (session.notes) {
    const m = session.notes.match(/payment[:\s]+([^\n,;]+)/i);
    if (m) return m[1].trim();
  }
  return 'PayPal';
}

// ─── Session → PaymentRecord ──────────────────────────────────────────────────

function sessionToPaymentRecord(session: MissionSession): PaymentRecord | null {
  // Only sessions with earnings OR that are in a payment-trackable state
  const hasEarnings = session.earningsActual != null && session.earningsActual > 0;
  const isTerminal  = TERMINAL_STATUSES.includes(session.status);
  const isWorking   = session.status === 'Working' || session.status === 'Paused';

  if (!hasEarnings && !isWorking && session.status !== 'Submitted') return null;

  const expectedPay    = session.earningsActual ?? session.earningsEstimated ?? 0;
  if (expectedPay <= 0) return null;

  const paymentStatus  = derivePaymentStatus(session);
  const expectedDate   = estimatedPaymentDate(session);
  const actualDate     = paymentStatus === 'completed'
    ? (session.endedAt?.slice(0, 10) ?? expectedDate)
    : undefined;

  return {
    id:                   `pay_${session.sessionId}`,
    missionId:            session.sessionId,
    companyId:            session.companyId ?? 'unknown',
    companyName:          session.companyName ?? 'Unknown',
    expectedPay,
    actualPay:            paymentStatus === 'completed' && hasEarnings
                          ? session.earningsActual!
                          : undefined,
    expectedPaymentDate:  expectedDate,
    actualPaymentDate:    actualDate,
    paymentStatus,
    paymentMethod:        inferPaymentMethod(session),
    approvalStatus:       paymentStatus === 'waiting' ? 'Awaiting Review'
                        : paymentStatus === 'completed' ? 'Approved'
                        : undefined,
    submissionDate:       isTerminal ? session.endedAt?.slice(0, 10) : undefined,
    completionDate:       paymentStatus === 'completed' ? session.endedAt?.slice(0, 10) : undefined,
    paymentNotes:         session.notes ?? undefined,
    lastUpdated:          session.lastModified,
  };
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Build a complete LivePaymentData bundle from the operator's session history.
 *
 * @param monthlyGoal  Operator's monthly income target (from operatorConfig / weeklyGoal × 4).
 */
export function buildLivePaymentData(monthlyGoal: number): LivePaymentData {
  const sessions = getAllSessions();

  // ── Filter to payment-relevant sessions ────────────────────────────────────
  const records: PaymentRecord[] = [];
  for (const s of sessions) {
    const rec = sessionToPaymentRecord(s);
    if (rec) records.push(rec);
  }

  const hasData = records.length > 0;
  const today   = isoToday();

  // ── Monthly stats (current calendar month) ─────────────────────────────────
  const thisMonthPrefix = today.slice(0, 7); // "YYYY-MM"

  const monthSessions = sessions.filter(s =>
    s.startedAt.startsWith(thisMonthPrefix) && s.earningsActual != null
  );
  const monthEarned = monthSessions
    .filter(s => s.paymentStatus === 'Immediate' || s.status === 'Paid')
    .reduce((sum, s) => sum + (s.earningsActual ?? 0), 0);
  const monthPending = monthSessions
    .filter(s => s.paymentStatus === 'Pending' || s.status === 'Submitted')
    .reduce((sum, s) => sum + (s.earningsActual ?? 0), 0);
  const monthProjected = Math.round((monthEarned + monthPending) * 100) / 100;

  const monthly: MonthlyPaymentStats = {
    goal:      monthlyGoal > 0 ? monthlyGoal : 700,
    earned:    Math.round(monthEarned   * 100) / 100,
    pending:   Math.round(monthPending  * 100) / 100,
    projected: monthProjected,
  };

  // ── Weekly bar chart (Mon → Sun of the current week) ─────────────────────
  const monday = thisWeekMonday();
  const weekly: PaymentWeekBar[] = WEEK_ORDER.map((dayLetter, i) => {
    const dayDate = new Date(monday);
    dayDate.setDate(monday.getDate() + i);
    const dayISO = dayDate.toISOString().slice(0, 10);

    const daySessions = sessions.filter(s => s.startedAt.startsWith(dayISO));
    const earned = daySessions
      .filter(s => s.paymentStatus === 'Immediate' || s.status === 'Paid')
      .reduce((sum, s) => sum + (s.earningsActual ?? 0), 0);
    const projected = daySessions
      .filter(s => s.paymentStatus === 'Pending' || s.status === 'Submitted' || s.status === 'Working')
      .reduce((sum, s) => sum + (s.earningsActual ?? s.earningsEstimated ?? 0), 0);

    return { day: dayLetter, earned: Math.round(earned * 100) / 100, projected: Math.round(projected * 100) / 100 };
  });

  // ── This-week totals for the metric box ────────────────────────────────────
  const weekStart = monday.toISOString().slice(0, 10);
  const weekEnd   = isoDateOffset(7);
  const weekEarnedSessions = sessions.filter(s => {
    const d = s.startedAt.slice(0, 10);
    return d >= weekStart && d <= weekEnd && s.earningsActual != null && s.earningsActual > 0;
  });
  const thisWeekEarned = weekEarnedSessions.reduce((sum, s) => sum + (s.earningsActual ?? 0), 0);
  const thisWeekCount  = weekEarnedSessions.length;

  const thisWeek = {
    earned: Math.round(thisWeekEarned * 100) / 100,
    count:  thisWeekCount,
  };

  // ── Coming this week (next 14 days with pending/expected payments) ─────────
  const twoWeeksOut = isoDateOffset(14);
  const comingThisWeek: ComingThisWeekItem[] = records
    .filter(r =>
      r.paymentStatus !== 'completed' &&
      r.expectedPaymentDate >= today &&
      r.expectedPaymentDate <= twoWeeksOut
    )
    .sort((a, b) => a.expectedPaymentDate.localeCompare(b.expectedPaymentDate))
    .slice(0, 5)
    .map(r => ({
      dateLabel:   formatDateLabel(r.expectedPaymentDate),
      companyName: r.companyName,
      status:      paymentStatusLabel(r.paymentStatus),
      amount:      r.expectedPay,
    }));

  // ── Recent payments (last 60 days, confirmed paid) ─────────────────────────
  const sixtyDaysAgo = isoDateOffset(-60);
  const recentPayments: RecentPaymentItem[] = sessions
    .filter(s =>
      (s.paymentStatus === 'Immediate' || s.status === 'Paid') &&
      s.earningsActual != null && s.earningsActual > 0 &&
      (s.endedAt ?? s.startedAt).slice(0, 10) >= sixtyDaysAgo
    )
    .sort((a, b) => (b.endedAt ?? b.startedAt).localeCompare(a.endedAt ?? a.startedAt))
    .slice(0, 6)
    .map(s => ({
      companyName: s.companyName ?? 'Unknown',
      date:        formatDisplayDate((s.endedAt ?? s.startedAt).slice(0, 10)),
      amount:      Math.round((s.earningsActual ?? 0) * 100) / 100,
      method:      inferPaymentMethod(s),
    }));

  // ── Actions derived from session state ─────────────────────────────────────
  const actions: PaymentActionItem[] = [];

  // Overdue pending (>14 days since ended with Pending payment status)
  const overdueSessions = sessions.filter(s => {
    if (s.paymentStatus !== 'Pending') return false;
    if (!s.endedAt) return false;
    const daysAgo = (Date.now() - new Date(s.endedAt).getTime()) / 86_400_000;
    return daysAgo > 14;
  });
  for (const s of overdueSessions.slice(0, 2)) {
    actions.push({
      title:    `Follow up with ${s.companyName}`,
      sub:      'Payment overdue — check platform dashboard',
      priority: 'HIGH',
    });
  }

  // Submitted work (>5 days with no payment update)
  const pendingSubmissions = sessions.filter(s => {
    if (s.status !== 'Submitted') return false;
    if (!s.endedAt) return false;
    const daysAgo = (Date.now() - new Date(s.endedAt).getTime()) / 86_400_000;
    return daysAgo > 5;
  });
  for (const s of pendingSubmissions.slice(0, 2)) {
    actions.push({
      title:    `Check ${s.companyName} approval status`,
      sub:      'Work submitted — awaiting review',
      priority: 'MEDIUM',
    });
  }

  // Sessions that earned but never logged payment method
  const unknownMethod = sessions.filter(s =>
    s.earningsActual != null && s.earningsActual > 0 &&
    TERMINAL_STATUSES.includes(s.status) &&
    s.paymentStatus === 'Unknown'
  );
  if (unknownMethod.length > 0) {
    actions.push({
      title:    'Update payment status for recent missions',
      sub:      `${unknownMethod.length} session${unknownMethod.length > 1 ? 's' : ''} with unknown payment outcome`,
      priority: 'LOW',
    });
  }

  return {
    records,
    thisWeek,
    monthly,
    weekly,
    comingThisWeek,
    recentPayments,
    actions: actions.slice(0, 4),
    hasData,
  };
}
